import s from './RestaurantFilters.module.sass'

import FilterMap from './ui/FilterMap/FilterMap'

import React, { FC, memo, useEffect } from 'react'
import { globalStore, RootState, useAppDispatch, useAppSelector } from '@app/model/store/client'

import { applyFilterAndUpdate, applyFilters } from '@features/Restaurant/RestaurantFilters/model/actions'
import { FilterButtonsTemplate } from '@features/Restaurant/RestaurantFilters/ui/FilterButtonsTemplate'
import { FilterListTemplate } from '@features/Restaurant/RestaurantFilters/ui/FilterListTemplate'
import { FilterOnceTemplate } from '@features/Restaurant/RestaurantFilters/ui/FilterOnceTemplate'
import { RestaurantFiltersSkeleton } from '@features/Restaurant/RestaurantFilters/index'
import { useFilters } from '@features/Restaurant/RestaurantFilters/helpers'

import {
  TypeFilterByKeyListCheckboxes,
  TypeFiltersByKeyAll,
  TypeFiltersByKeyListButtons,
  TypeFiltersByKeyOnce,
  TypeOutput,
} from '@features/Restaurant/RestaurantFilters/model/types'

import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import useKuperFlags from '@shared/lib/hooks/useKuperFlags'
import { log } from '@shared/lib/helpers/debugLog'

type TypeFilters = TypeFiltersByKeyAll

const FILTER_TYPE_LIST: TypeFilterByKeyListCheckboxes[] = ['establishment', 'kitchen', 'metro']
const FILTER_TYPE_BUTTONS: TypeFiltersByKeyListButtons[] = ['price', 'rating']
const FILTER_TYPE_ONCE_BASE: TypeFiltersByKeyOnce[] = ['afishaSelected', 'hasBooking', 'openNow']

/**
 * @function getFilterTypeOnce
 * возвращает список once-фильтров: с delivery при kuper, иначе базовый
 */
const getFilterTypeOnce = (isActiveKuper: boolean): TypeFiltersByKeyOnce[] => {
  const variants: [boolean, TypeFiltersByKeyOnce[]][] = [
    [true, [...FILTER_TYPE_ONCE_BASE.slice(0, -2), 'delivery', ...FILTER_TYPE_ONCE_BASE.slice(-2)]],
    [false, FILTER_TYPE_ONCE_BASE],
  ]
  return variants.find(([flag]) => flag === isActiveKuper)?.[1] ?? FILTER_TYPE_ONCE_BASE
}

interface Props {
  preloadingResults?: boolean
  handleLoading?: (value: boolean) => void
  handleOutputData?: (data: TypeOutput) => void
  selected: string[] // slugs
  className?: string
  citySlug?: string
  hideDivider?: boolean
  filters?: TypeFilters[] | 'all'
  reloadMap?: boolean
}

const RestaurantFilters: FC<Props> = (params) => {
  const {
    preloadingResults = true,
    handleLoading,
    handleOutputData,
    selected,
    hideDivider,
    className = '',
    citySlug: citySlugProp = '',
    filters = 'all',
    reloadMap,
  } = params

  const storeCityCoordinates = useAppSelector((store) => store.global.cityRead?.coordinates)
  const currentCity = useAppSelector((state) => state.global.currentCity)
  const currentCitySlug = currentCity?.slug ?? ''
  const [stateFilters] = useFilters(selected, preloadingResults)
  const dispatch = useAppDispatch()
  const [isActiveKuperProd, isActiveKuperDev] = useKuperFlags()
  const isActiveKuper = ['msk', 'spb'].includes(currentCitySlug) && (isActiveKuperDev || isActiveKuperProd)

  useEffect(() => {
    handleLoading && handleLoading(stateFilters.output.loading)
  }, [stateFilters.output.loading])

  useEffect(() => {
    log('restaurant_filters', 'render state', {
      ready: stateFilters.ready,
      filterCatalogsReady: stateFilters.filterCatalogsReady,
      visible: stateFilters.visible,
      hasCoordinates: !!storeCityCoordinates,
      citySlug: currentCity?.slug,
    })
  }, [
    stateFilters.ready,
    stateFilters.filterCatalogsReady,
    stateFilters.visible,
    storeCityCoordinates,
    currentCity?.slug,
  ])

  const handleSendOutputData = async () => {
    if (!stateFilters.filterCatalogsReady) return
    await dispatch(reloadMap ? applyFilterAndUpdate() : applyFilters())
    handleOutputData?.((globalStore.getState() as RootState).features.restaurant_filters.output)
  }

  const hasCitySlug = Boolean(citySlugProp || currentCitySlug)
  const missingCatalog = !stateFilters.filterCatalogsReady || !storeCityCoordinates
  const isNotReady = !hasCitySlug && missingCatalog
  const isShowFilterMap = storeCityCoordinates && (filters == 'all' || filters.includes('map'))

  const filterKeys = (filters === 'all' ? Object.keys(stateFilters.filters) : filters) as TypeFiltersByKeyAll[]

  const visibleFilterKeys = filterKeys.filter((key) => {
    const isVisible = stateFilters.filters[key]?.visible
    const isInScope = filters === 'all' || (filters as TypeFiltersByKeyAll[]).includes(key)
    return Boolean(isVisible) && isInScope
  })

  const filterTypeOnce = getFilterTypeOnce(isActiveKuper)

  /**
   * @function renderFilterItem
   * выбирает шаблон по типу фильтра и рендерит
   */
  const renderFilterItem = (typeFilter: TypeFiltersByKeyAll) => {
    const onceView = filterTypeOnce.includes(typeFilter as TypeFiltersByKeyOnce) && (
      <>
        <FilterOnceTemplate typeFilter={typeFilter as TypeFiltersByKeyOnce} handleChange={handleSendOutputData} />
        {!hideDivider && typeFilter === 'openNow' && <div className={s.divider}></div>}
      </>
    )

    const listView = FILTER_TYPE_LIST.includes(typeFilter as TypeFilterByKeyListCheckboxes) && (
      <FilterListTemplate
        typeFilter={typeFilter as TypeFilterByKeyListCheckboxes}
        handleSave={handleSendOutputData}
        handleRemove={handleSendOutputData}
      />
    )

    const buttonsView = FILTER_TYPE_BUTTONS.includes(typeFilter as TypeFiltersByKeyListButtons) && (
      <FilterButtonsTemplate
        typeFilter={typeFilter as TypeFiltersByKeyListButtons}
        handleSave={handleSendOutputData}
        handleRemove={handleSendOutputData}
      />
    )

    const view = [onceView, listView, buttonsView].find(Boolean)
    return view ? <React.Fragment key={`${typeFilter}_wrap`}>{view}</React.Fragment> : null
  }

  return isNotReady ? (
    <RestaurantFiltersSkeleton className={className} />
  ) : (
    <BlockBoundary>
      <div
        className={`${s.wrapper} ${className}`}
        data-is-visible={stateFilters.visible || hasCitySlug}
        data-baseline-slot='listing.filters'
        data-baseline-enhance='listing.filters'>
        {isShowFilterMap && <FilterMap />}
        {visibleFilterKeys.map(renderFilterItem)}
      </div>
    </BlockBoundary>
  )
}

export default memo(RestaurantFilters)
